<script setup lang="ts">
import Dropdown from 'primevue/dropdown';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  watch,
} from 'vue';

import Button from 'lib/components/button/Button.vue';
import { Data } from 'lib/components/datatable/DataTable.vue.d';
import Icon from 'lib/components/icon/Icon.vue';
import Menu from 'lib/components/menu/Menu.vue';
import WVMenuItem from 'lib/components/menuitem/MenuItem.vue';
import eventBus, { Events } from 'lib/event-bus';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';

import {
  ButtonBulkActionEmits,
  ButtonBulkActionProps,
} from './ButtonBulkAction.vue.d';
import { filterVisibleMenu } from '../menu/helpers';
import { MenuItem } from '../menuitem';

const props = withDefaults(defineProps<ButtonBulkActionProps>(), {
  tableName: 'datatable',
  useButton: true,
  selectedData: () => [],
  options: () => [],
});

const emit = defineEmits<ButtonBulkActionEmits>();
const { defaultProps, locale } = useWangsVueConfig('ButtonBulkAction');
const config = mergePropsWithDefaults(props, defaultProps);

onMounted(() => {
  window.addEventListener('disableBulkAction', disableBulkAction);

  eventBus.on('data-table:update-selected-data', handleUpdateSelectedData);
  eventBus.on('data-table:update-total-record', updateTotalRecordsHandler);
});

onUnmounted(() => {
  window.removeEventListener('disableBulkAction', disableBulkAction);
  eventBus.off('data-table:update-total-record', handleUpdateSelectedData);
  eventBus.off('data-table:update-selected-data', updateTotalRecordsHandler);
});

const menu = ref<any>();
const wrapper = ref<HTMLDivElement>();
const selectedOption = shallowRef<MenuItem>();
const totalRecords = shallowRef<number>(0);
const isAllDataSelected = shallowRef<boolean>(false);
const isDisabled = shallowRef<boolean>(false);
const dataSelected = ref<Data[] | undefined>(props.selectedData);

const showSelectAllButton = computed(
  () =>
    dataSelected.value?.length && !isDisabled.value && totalRecords.value > 0,
);

const selectAllData = (): void => {
  /**
   * Trigger fetch all data from data table.
   *
   */
  window.dispatchEvent(
    new CustomEvent('fetchAllData', { detail: props.tableName }),
  );

  eventBus.emit('data-table:select-all-record', { tableName: props.tableName });
};

const unSelectAllData = (): void => {
  emit('update:selectedData', []);
  eventBus.emit('data-table:clear-selected-data', {
    tableName: props.tableName,
  });
};

const disableBulkAction = (event: CustomEvent): void => {
  if (props.tableName === event.detail.tableName)
    isDisabled.value = event.detail.value;
};

const toggleMenu = (e: Event): void => {
  menu.value?.toggle(e);
};

/**
 * Handle the event when the total records is updated from the data table.
 * @param event custom event with totalRecords data.
 */
const updateTotalRecordsHandler = (
  event: Events['data-table:update-total-record'],
): void => {
  if (props.tableName === event?.tableName) {
    totalRecords.value = event?.total;
  }
};

const handleUpdateSelectedData = (
  e: Events['data-table:update-selected-data'],
): void => {
  if (e.tableName === props.tableName) {
    dataSelected.value = e.data;
    emit('update:selectedData', e.data ?? []);
  }
};

/**
 * If there is no addition content slot, the command will be executed on option selected.
 * Otherwise, display additional slot and apply button. Command will be executed on Apply clicked.
 */
const onOptionSelected = async (option: MenuItem): Promise<void> => {
  /**
   * If the additional slot used, the Apply button will shown and the bulk action label changed.
   * Otherwise, bulk action label should not changed.
   */
  if (option.useAdditionSlot) {
    selectedOption.value = option;

    await nextTick(); // Waits the Additional Slot rendered.

    const firstSlotElement = wrapper.value?.children[1] as HTMLElement;

    const firstSlotInput = firstSlotElement?.querySelector(
      'input',
    ) as HTMLInputElement;

    /**
     * Clicking element will make the menu overlay dissapear.
     * Focusing input wil improve user experience.
     */
    if (firstSlotInput) {
      firstSlotInput?.click();
      firstSlotInput?.focus();
    }
  } else if (option.command) {
    option.command({
      originalEvent: undefined,
      item: option,
    });
    menu.value?.hide();
    selectedOption.value = undefined;
  } else if (option.url) {
    window.open(option.url, option.target);
  } else if (option.route) {
    window.history.pushState(undefined, '', option.route);
  }
};

const onApplyButtonClicked = (event: Event): void => {
  selectedOption.value?.command?.({
    originalEvent: event,
    item: selectedOption.value,
  });

  selectedOption.value = undefined;
  emit('apply');
};

watch(
  [dataSelected, totalRecords],
  ([data, total]) => {
    if (!data?.length) selectedOption.value = undefined;
    isAllDataSelected.value = data?.length === total;
  },
  { deep: true },
);

watch(
  () => props.totalRecords,
  (total) => (totalRecords.value = total ?? 0),
  { immediate: true },
);
</script>

<template>
  <div
    v-bind="$preset.buttonbulkaction.root"
    ref="wrapper"
    v-show="dataSelected?.length"
  >
    <Menu
      ref="menu"
      v-if="config.selectMenuType === 'overlay-panel' && dataSelected?.length"
      :model="options"
      popup
    />

    <template v-if="config.selectMenuType === 'dropdown'">
      <Dropdown
        v-if="selectedData?.length"
        :disabled="props.disabled || isDisabled || !selectedData?.length"
        :model-value="selectedOption"
        :options="
          filterVisibleMenu(
            options,
            $wangsvue.config.locale.components.Menu.noOptionLabel,
          )
        "
        :pt="$preset.buttonbulkaction.dropdownmenu"
        @update:model-value="onOptionSelected($event)"
        option-label="label"
        outlined
        placeholder="Bulk Action"
      >
        <template #dropdownicon="{ class: iconClass }">
          <Icon :class="[iconClass]" icon="arrow-down" />
        </template>

        <template #option="{ option }">
          <WVMenuItem
            :item="option"
            :router-props="{
              action: $preset.menu.action,
              label: $preset.menu.label,
              icon: $preset.menu.icon,
            }"
          />
        </template>
      </Dropdown>

      <slot :selected-option="selectedOption" name="addition" />

      <Button
        v-show="selectedOption?.useAdditionSlot"
        @click="onApplyButtonClicked"
        v-bind="$preset.buttonbulkaction.applybutton"
        label="Apply"
      />
    </template>

    <span
      v-show="dataSelected?.length"
      v-bind="$preset.buttonbulkaction.selectionmessage"
    >
      {{
        locale.currentSelectionMessage.replace(
          '{length} {naming}',
          `${dataSelected.length.toString()} ${config.naming}`,
        )
      }}
    </span>

    <Button
      v-show="showSelectAllButton"
      @click="selectAllData"
      v-bind="
        $preset.buttonbulkaction.buttonselectall({
          context: { selectedAll: isAllDataSelected },
        })
      "
      text
    >
      <template v-if="isAllDataSelected">
        {{ locale.allRecordSelected.replace('{naming}', config.naming) }}
      </template>

      <template v-else>
        {{
          locale.selectAllRecords.replace(
            '{totalRecords}',
            totalRecords.toString(),
          )
        }}
      </template>
    </Button>

    <Button
      v-if="config.selectMenuType === 'overlay-panel'"
      @click="toggleMenu"
      v-bind="$preset.buttonbulkaction.bulkactiontoggle"
      icon="ellipsis-h"
      outlined
      severity="secondary"
      size="small"
    />

    <Button
      @click="unSelectAllData"
      v-bind="$preset.buttonbulkaction.buttonclearselection"
      icon="close"
      severity="danger"
      text
    />
  </div>
</template>
